import { ShieldCheck, Brush, Droplets, MessageSquare } from "lucide-react";
import { processSteps, images } from "@/config/site";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";

const commitments = [
  {
    icon: ShieldCheck,
    title: "Workmanship & quality",
    text: "Coats applied in the right order with proper drying time, edges cut cleanly by hand, and a final inspection against the agreed scope — not a quick glance from the driveway.",
  },
  {
    icon: Brush,
    title: "Surface preparation",
    text: "Washing, scraping, sanding, filling, caulking and priming are the majority of a lasting paint job. Our written estimates spell out the preparation included so nothing is quietly skipped.",
  },
  {
    icon: Droplets,
    title: "Cleanliness & property protection",
    text: "Floors sheeted, furniture covered, landscaping protected and tools organized. Work areas are tidied at the end of every day — your home never has to feel like a job site.",
  },
  {
    icon: MessageSquare,
    title: "Customer communication",
    text: "You'll know the schedule before we start, hear about anything unexpected as it comes up, and walk the finished project with us before we call it complete.",
  },
];

export default function Process() {
  return (
    <>
      <SEO
        title="Our Process | How North Ember Painting Works — Delta, BC"
        description="From first inquiry to final walkthrough: how North Ember Painting plans, prepares, paints and finishes every project in Delta, Ladner, Tsawwassen and Surrey."
        path="/process"
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Our Process", path: "/process" }])]}
      />
      <PageHero
        eyebrow="Our process"
        title="No surprises — just a clear path from inquiry to walkthrough"
        intro="Every project follows the same eight steps. You'll always know what's happening, what's next, and what's included."
        image="painterPrep"
      />

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <ol className="space-y-0">
            {processSteps.map((step, i) => (
              <Reveal key={step.title} delay={i * 40}>
                <li className="relative flex gap-6 pb-12 last:pb-0">
                  {i < processSteps.length - 1 && (
                    <span className="absolute left-[23px] top-12 h-full w-px bg-slate-200" aria-hidden="true" />
                  )}
                  <span className="z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-ink font-display text-lg font-bold text-ember shadow-md">
                    {i + 1}
                  </span>
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 className="font-display text-xl font-semibold text-slate-900">{step.title}</h2>
                    <p className="mt-2 text-base leading-relaxed text-slate-600">{step.text}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-ink py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ember">Our commitments</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              The standards behind every project
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {commitments.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}>
                <div className="tilt-card h-full rounded-2xl border border-white/10 bg-white/[0.04] p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-ember/40">
                  <c.icon className="h-7 w-7 text-ember" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-xl font-semibold text-slate-50">{c.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-slate-400">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-2xl shadow-xl">
              <img src={images.painterCeiling.url} alt={images.painterCeiling.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <p className="bg-white px-4 py-2.5 text-xs text-slate-500">Illustrative example — not a photo of our team.</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Good painting is mostly preparation
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Anyone can roll colour onto a wall. The difference between a finish that lasts two years and one that
              lasts ten is what happens before the can opens — and how carefully your home is treated while the
              work happens. That's where we spend our time and your money wisely.
            </p>
          </Reveal>
        </div>
      </section>

      <CTA title="See the difference a real process makes" />
    </>
  );
}
